import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, type AlertIncident, type AlertRule, type MetricCatalogEntry } from "@/lib/api";
import { ErrorBox, PageHeader, Spinner, StatTile } from "@/components/ui";

export function ObservabilityAlertsPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [incidents, setIncidents] = useState<AlertIncident[]>([]);
  const [catalog, setCatalog] = useState<MetricCatalogEntry[]>([]);
  const [name, setName] = useState("");
  const [metric, setMetric] = useState("error_rate");
  const [threshold, setThreshold] = useState("5");

  const reload = async () => {
    if (!id) return;
    const [r, i, c] = await Promise.all([
      api.listAlertRules(id),
      api.listAlertIncidents(id),
      api.metricCatalog(id),
    ]);
    setRules(r.data);
    setIncidents(i.data);
    setCatalog(c.data);
  };

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        await reload();
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <Spinner label="Loading alerts…" />;

  return (
    <div>
      <PageHeader
        eyebrow="Observability"
        title="Alerts"
        description="Threshold rules with deduplicated incidents. Evaluation runs in the background."
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label="Rules" value={rules.length} />
        <StatTile
          label="Open incidents"
          value={incidents.filter((i) => i.status === "open").length}
        />
        <StatTile label="Metrics" value={catalog.length} />
      </div>

      <div className="card mb-6 p-4">
        <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Create rule</h3>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-text-muted">
            Name
            <input
              className="mt-1 block rounded border border-border-standard bg-surface-container-lowest px-2 py-1.5 text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="text-xs text-text-muted">
            Metric
            <select
              className="mt-1 block rounded border border-border-standard bg-surface-container-lowest px-2 py-1.5 text-sm"
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
            >
              {(catalog.length
                ? catalog.map((c) => c.id)
                : ["error_rate", "p95_latency_ms", "request_volume"]
              ).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-text-muted">
            Threshold
            <input
              className="mt-1 block w-24 rounded border border-border-standard bg-surface-container-lowest px-2 py-1.5 text-sm"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="btn-primary"
            onClick={() =>
              void (async () => {
                try {
                  await api.createAlertRule(id, {
                    name,
                    metric,
                    operator: "gt",
                    threshold: Number(threshold),
                    window_minutes: 5,
                    severity: "warning",
                  });
                  setName("");
                  await reload();
                } catch (e) {
                  setError(e);
                }
              })()
            }
          >
            Add rule
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() =>
              void api.evaluateAlerts(id).then(() => reload()).catch((e) => setError(e))
            }
          >
            Evaluate now
          </button>
        </div>
      </div>

      <div className="mb-6 card p-4">
        <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Rules</h3>
        {rules.length === 0 ? (
          <p className="text-sm text-text-muted">No rules yet.</p>
        ) : (
          <table className="data w-full text-sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Metric</th>
                <th>Op</th>
                <th className="text-right">Threshold</th>
                <th>Severity</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="font-mono text-xs">{r.metric}</td>
                  <td className="mono-chip">{r.operator}</td>
                  <td className="text-right tabular-nums">{r.threshold}</td>
                  <td>{r.severity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card p-4">
        <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Incidents</h3>
        {incidents.length === 0 ? (
          <p className="text-sm text-text-muted">No incidents.</p>
        ) : (
          <table className="data w-full text-sm">
            <thead>
              <tr>
                <th>Opened</th>
                <th>Status</th>
                <th>Message</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((i) => (
                <tr key={i.id}>
                  <td className="text-xs text-text-muted">{new Date(i.opened_at).toLocaleString()}</td>
                  <td>
                    <span className="mono-chip">{i.status}</span>
                  </td>
                  <td className="text-xs">{i.message}</td>
                  <td className="space-x-1 text-right">
                    {i.status === "open" ? (
                      <>
                        <button
                          type="button"
                          className="btn-secondary !py-1 text-xs"
                          onClick={() =>
                            void api.ackIncident(id, i.id).then(() => reload()).catch(setError)
                          }
                        >
                          Ack
                        </button>
                        <button
                          type="button"
                          className="btn-secondary !py-1 text-xs"
                          onClick={() =>
                            void api.resolveIncident(id, i.id).then(() => reload()).catch(setError)
                          }
                        >
                          Resolve
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
